export const COURSE_CONTENT_SCOPES = [
	'universal',
	'regional',
	'national',
] as const

export type CourseContentScope = (typeof COURSE_CONTENT_SCOPES)[number]

export const COURSE_JURISDICTION_TYPES = ['country', 'region'] as const

export type CourseJurisdictionType = (typeof COURSE_JURISDICTION_TYPES)[number]

export const REGULATORY_REVIEW_STATUSES = [
	'not_required',
	'current',
	'needs_review',
	'deprecated',
] as const

export type RegulatoryReviewStatus = (typeof REGULATORY_REVIEW_STATUSES)[number]

export type CourseInternationalizationIssueCode =
	| 'UNIVERSAL_WITH_JURISDICTIONS'
	| 'NATIONAL_WITHOUT_COUNTRY'
	| 'REGIONAL_WITHOUT_REGION'
	| 'PRIMARY_JURISDICTION_NOT_LISTED'

export interface CourseInternationalizationIssue {
	code: CourseInternationalizationIssueCode
	message: string
}

export interface CourseInternationalizationInput {
	contentScope?: CourseContentScope | null
	primaryJurisdiction?: string | null
	jurisdictions?: readonly string[] | null
	jurisdictionTypes?: Record<string, CourseJurisdictionType | undefined> | null
}

export interface CourseVisibilityInput {
	contentScope?: CourseContentScope | null
	jurisdictions?: readonly string[] | null
	selectedJurisdiction?: string | null
	regionMemberships?: Record<string, readonly string[] | undefined>
}

export interface CourseApplicabilityInput {
	contentScope?: string | null
	primaryJurisdiction?: string | null
	regulatoryReviewStatus?: string | null
}

export interface CourseApplicabilityView {
	scope: CourseContentScope
	jurisdiction: string | null
	/** Whether the UI should state where a non-universal course applies. */
	showApplies: boolean
	/** Surfaced only for national/regional courses that need or lack review. */
	reviewStatus: RegulatoryReviewStatus | null
}

function normalizeContentScope(value: string | null | undefined) {
	return COURSE_CONTENT_SCOPES.includes(value as CourseContentScope)
		? (value as CourseContentScope)
		: 'universal'
}

function normalizeRegulatoryReviewStatus(value: string | null | undefined) {
	return REGULATORY_REVIEW_STATUSES.includes(value as RegulatoryReviewStatus)
		? (value as RegulatoryReviewStatus)
		: 'not_required'
}

/**
 * Presentation-agnostic applicability summary for public route/program detail.
 * Returns structured flags only; i18n labels are resolved by the view layer so
 * this stays brand- and locale-neutral. Universal courses never advertise a
 * jurisdiction; national/regional courses surface their primary jurisdiction and
 * a review status worth showing (anything other than `not_required`).
 */
export function resolveCourseApplicability(
	input: CourseApplicabilityInput,
): CourseApplicabilityView {
	const scope = normalizeContentScope(input.contentScope)
	const jurisdiction = normalizeJurisdictionCode(input.primaryJurisdiction)
	const reviewStatus = normalizeRegulatoryReviewStatus(
		input.regulatoryReviewStatus,
	)
	const showApplies = scope !== 'universal' && jurisdiction !== null

	return {
		scope,
		jurisdiction,
		showApplies,
		reviewStatus:
			showApplies && reviewStatus !== 'not_required' ? reviewStatus : null,
	}
}

export function normalizeJurisdictionCode(value: string | null | undefined) {
	const normalized = value?.trim().toUpperCase()
	return normalized ? normalized : null
}

function normalizeJurisdictionCodes(
	values: readonly string[] | null | undefined,
) {
	return Array.from(
		new Set(
			(values ?? [])
				.map((value) => normalizeJurisdictionCode(value))
				.filter((value): value is string => Boolean(value)),
		),
	)
}

function normalizeJurisdictionTypeMap(input: CourseInternationalizationInput) {
	const result = new Map<string, CourseJurisdictionType>()
	for (const jurisdiction of normalizeJurisdictionCodes(input.jurisdictions)) {
		const explicitType = input.jurisdictionTypes?.[jurisdiction]
		result.set(
			jurisdiction,
			explicitType ?? (jurisdiction.length === 2 ? 'country' : 'region'),
		)
	}
	return result
}

export function getCourseInternationalizationIssues(
	input: CourseInternationalizationInput,
): CourseInternationalizationIssue[] {
	const scope = input.contentScope ?? 'universal'
	const jurisdictions = normalizeJurisdictionCodes(input.jurisdictions)
	const jurisdictionTypes = normalizeJurisdictionTypeMap(input)
	const primaryJurisdiction = normalizeJurisdictionCode(
		input.primaryJurisdiction,
	)
	const issues: CourseInternationalizationIssue[] = []

	if (scope === 'universal' && jurisdictions.length > 0) {
		issues.push({
			code: 'UNIVERSAL_WITH_JURISDICTIONS',
			message: 'Universal courses must not declare jurisdiction applicability.',
		})
	}

	if (
		scope === 'national' &&
		!jurisdictions.some(
			(jurisdiction) => jurisdictionTypes.get(jurisdiction) === 'country',
		)
	) {
		issues.push({
			code: 'NATIONAL_WITHOUT_COUNTRY',
			message:
				'National courses must declare at least one country jurisdiction.',
		})
	}

	if (
		scope === 'regional' &&
		!jurisdictions.some(
			(jurisdiction) => jurisdictionTypes.get(jurisdiction) === 'region',
		)
	) {
		issues.push({
			code: 'REGIONAL_WITHOUT_REGION',
			message:
				'Regional courses must declare at least one region jurisdiction.',
		})
	}

	if (primaryJurisdiction && !jurisdictions.includes(primaryJurisdiction)) {
		issues.push({
			code: 'PRIMARY_JURISDICTION_NOT_LISTED',
			message:
				'Primary jurisdiction must also be present in the course jurisdiction list.',
		})
	}

	return issues
}

/**
 * In-memory mirror of the catalog SQL predicate
 * `buildPublicCatalogJurisdictionWhere`. Keep the two aligned: this helper
 * additionally supports `regionMemberships` (region code → member countries),
 * which the SQL path does not yet implement. Until membership is wired into the
 * query, prefer the SQL predicate for catalog filtering and do not rely on the
 * `regionMemberships` branch for production visibility decisions.
 */
export function isCourseVisibleForJurisdiction(input: CourseVisibilityInput) {
	const scope = input.contentScope ?? 'universal'
	const jurisdictions = normalizeJurisdictionCodes(input.jurisdictions)
	const selectedJurisdiction = normalizeJurisdictionCode(
		input.selectedJurisdiction,
	)

	if (scope === 'universal') return true
	if (!selectedJurisdiction) return false

	if (scope === 'national') {
		return jurisdictions.includes(selectedJurisdiction)
	}

	if (scope === 'regional') {
		return jurisdictions.some((regionCode) => {
			if (regionCode === selectedJurisdiction) return true
			const members =
				input.regionMemberships?.[regionCode]?.map((member) =>
					normalizeJurisdictionCode(member),
				) ?? []
			return members.includes(selectedJurisdiction)
		})
	}

	return false
}
